import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, Plus, Upload } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { CustomSelect } from '~/components/ui/custom-select'
import { DateTimePicker } from '~/components/ui/date-picker'
import { ReadingsFilterBar } from './readings-filter-bar'
import { ReadingRow } from './reading-row'
import { TablePagination } from './table-pagination'
import { useReadingsTable } from './use-readings-table'

/** 데이터베이스 — 적재된 측정값을 표로 보고 수정·삭제(CRUD). 추가는 데이터 연동으로 이동. */
export function DatabaseSection({ dashboardId }: { dashboardId: string }) {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const {
        rows,
        total,
        offset,
        setOffset,
        labels,
        batchLabels,
        indicators,
        loading,
        busyId,
        editId,
        setEditId,
        editValue,
        setEditValue,
        adding,
        addSaving,
        addFieldId,
        setAddFieldId,
        addValue,
        setAddValue,
        addMeasuredAt,
        setAddMeasuredAt,
        fieldId,
        setFieldId,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        q,
        setQ,
        batchId,
        setBatchId,
        batches,
        hasFilter,
        resetFilters,
        startEdit,
        saveEdit,
        remove,
        openAdd,
        cancelAdd,
        canSaveAdd,
        saveAdd,
        from,
        to,
    } = useReadingsTable(dashboardId, searchParams.get('batch') ?? undefined)

    return (
        <section className='border-border bg-card rounded-md border p-5'>
            <div className='mb-3 flex flex-wrap items-center justify-between gap-3'>
                <div>
                    <h2 className='text-foreground text-sm font-semibold'>
                        적재된 측정값
                    </h2>
                    <p className='text-muted-foreground mt-0.5 text-xs'>
                        총 {total.toLocaleString()}건 · {from}–{to} 표시
                    </p>
                </div>
                <div className='flex gap-2'>
                    <Button
                        onClick={() =>
                            navigate(`/monitoring/${dashboardId}/settings`)
                        }>
                        <Upload />
                        데이터 적재
                    </Button>
                    <Button
                        variant='outline'
                        disabled={adding}
                        onClick={openAdd}>
                        <Plus />
                        레코드 추가
                    </Button>
                </div>
            </div>

            <ReadingsFilterBar
                fieldId={fieldId}
                setFieldId={setFieldId}
                indicators={indicators}
                q={q}
                setQ={setQ}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                batchId={batchId}
                setBatchId={setBatchId}
                batches={batches}
                hasFilter={hasFilter}
                resetFilters={resetFilters}
            />

            {loading ? (
                <p className='text-muted-foreground text-sm'>불러오는 중…</p>
            ) : rows.length === 0 && !adding ? (
                <p className='text-muted-foreground text-sm'>
                    {hasFilter
                        ? '조건에 맞는 측정값이 없어요. 필터를 바꿔 보세요.'
                        : '적재된 측정값이 없어요. 「데이터 적재」로 데이터를 올리거나 「레코드 추가」로 1건을 입력해 보세요.'}
                </p>
            ) : (
                <>
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='text-muted-foreground border-border border-b text-xs'>
                                    <th className='py-2 pr-3 text-left'>
                                        측정 시각
                                    </th>
                                    <th className='py-2 pr-3 text-left'>
                                        지표
                                    </th>
                                    <th className='py-2 pr-3 text-left'>값</th>
                                    <th className='py-2 pr-3 text-left'>
                                        출처
                                    </th>
                                    <th className='py-2 text-left'>조치</th>
                                </tr>
                            </thead>
                            <tbody>
                                {adding ? (
                                    <tr className='border-border bg-muted/30 border-b'>
                                        <td className='py-2 pr-3'>
                                            <DateTimePicker
                                                ariaLabel='측정 시각'
                                                value={addMeasuredAt}
                                                onChange={(v) =>
                                                    setAddMeasuredAt(v)
                                                }
                                                containerClassName='w-56'
                                            />
                                        </td>
                                        <td className='py-2 pr-3'>
                                            <CustomSelect
                                                ariaLabel='지표 선택'
                                                value={addFieldId}
                                                onValueChange={(v) =>
                                                    setAddFieldId(v)
                                                }
                                                placeholder='지표 선택'
                                                containerClassName='w-44'
                                                className='border-border h-8'
                                                searchable
                                                searchPlaceholder='지표 검색…'
                                                options={indicators.map(
                                                    (i) => ({
                                                        value: i.fieldId,
                                                        label: `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`,
                                                    })
                                                )}
                                            />
                                        </td>
                                        <td className='py-2 pr-3'>
                                            <input
                                                value={addValue}
                                                onChange={(e) =>
                                                    setAddValue(e.target.value)
                                                }
                                                placeholder='예: 4.5'
                                                className='border-border bg-card text-foreground h-8 w-28 rounded-md border px-2 text-sm'
                                            />
                                        </td>
                                        <td className='text-muted-foreground py-2 pr-3 text-xs'>
                                            수동
                                        </td>
                                        <td className='py-2'>
                                            <div className='flex gap-1'>
                                                <Button
                                                    size='sm'
                                                    disabled={!canSaveAdd}
                                                    onClick={saveAdd}>
                                                    {addSaving ? (
                                                        <Loader2 className='animate-spin' />
                                                    ) : null}
                                                    저장
                                                </Button>
                                                <Button
                                                    size='sm'
                                                    variant='outline'
                                                    disabled={addSaving}
                                                    onClick={cancelAdd}>
                                                    취소
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                                {rows.map((r) => (
                                    <ReadingRow
                                        key={r.id}
                                        r={r}
                                        labels={labels}
                                        batchLabels={batchLabels}
                                        editId={editId}
                                        setEditId={setEditId}
                                        editValue={editValue}
                                        setEditValue={setEditValue}
                                        busyId={busyId}
                                        startEdit={startEdit}
                                        saveEdit={saveEdit}
                                        remove={remove}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <TablePagination
                        from={from}
                        to={to}
                        total={total}
                        offset={offset}
                        setOffset={setOffset}
                    />
                </>
            )}
        </section>
    )
}
